import { OptionType } from "../../metadata/interfaces/classifier";

// jdn-hr certificate results carry a 3-level path, e.g.
// "NAU > FUN > 1st.Dredge.Operator" (cteCode > cgpCode > certificate). We turn
// the flat search results into a checkable tree so the user can tick a whole
// group (e.g. "NAU") and select every certificate underneath it in one click.
export const PATH_SEPARATOR = " > ";

// Selected filter values are joined with "+" before being sent to the backend,
// where they are split apart again on "+". Some certificate names contain a
// literal "+" (e.g. "NAU > CA > CPCS.A40+A61"), which would otherwise be
// mistaken for the value delimiter and split into a stray fragment ("A61"),
// causing unrelated certificates to match. We escape any literal "+" inside a
// value with this placeholder so it survives the join/split round-trip. The
// backend uses the same placeholder to restore it before matching. The token is
// made of URL-unreserved characters so encodeURIComponent leaves it untouched
// in transit, and is unique enough never to collide with real data.
export const PLUS_PLACEHOLDER = "__KLASSIFAI_PLUS__";

export const escapeFilterValue = (value: string): string =>
  value.split("+").join(PLUS_PLACEHOLDER);

export const unescapeFilterValue = (value: string): string =>
  value.split(PLUS_PLACEHOLDER).join("+");

// The response property that holds the full hierarchical path. Configurable via
// filterAble.query.pathProperty; defaults to the (unmodified) label.
export const DEFAULT_PATH_PROPERTY = "label";

// Group (cte / cgp) nodes need a value of their own so antd can track their
// checked state, but that value must never collide with a real certificate id
// nor be sent to the backend. We prefix them; combined with SHOW_CHILD on the
// TreeSelect, only leaf values ever leave the component.
const CTE_PREFIX = "__cte__:";
const CGP_PREFIX = "__cgp__:";

export interface CertificateTreeNode {
  title: string;
  value: string;
  children?: Array<CertificateTreeNode>;
}

const getPath = (option: OptionType, pathProperty: string): string => {
  const raw = option[pathProperty as keyof OptionType];
  return typeof raw === "string" && raw.length ? raw : option.label;
};

export const buildCertificateTree = (
  options: Array<OptionType>,
  pathProperty?: string,
): Array<CertificateTreeNode> => {
  const resolvedPathProperty = pathProperty ?? DEFAULT_PATH_PROPERTY;
  const cteNodes: Array<CertificateTreeNode> = [];
  const cteMap = new Map<string, CertificateTreeNode>();
  const cgpMap = new Map<string, CertificateTreeNode>();

  options.forEach((option) => {
    const path = getPath(option, resolvedPathProperty);
    const segments = path.split(PATH_SEPARATOR);

    // When a pathProperty is configured the backend stores (and filters on) the
    // path value, not the option id. The leaf value becomes the filter query
    // value, so it must be the path too; otherwise we keep the raw id like the
    // flat select.
    const leafValue = pathProperty ? path : option.value;

    // Fall back to a flat leaf when the path is not a full 3-level path so we
    // never silently drop an option.
    if (segments.length < 3) {
      cteNodes.push({
        title: option.visibleValue ?? option.label,
        value: leafValue,
      });
      return;
    }

    const [cteCode, cgpCode, ...rest] = segments;
    const certificateName = option.visibleValue ?? rest.join(PATH_SEPARATOR);

    let cteNode = cteMap.get(cteCode);
    if (!cteNode) {
      cteNode = {
        title: cteCode,
        value: `${CTE_PREFIX}${cteCode}`,
        children: [],
      };
      cteMap.set(cteCode, cteNode);
      cteNodes.push(cteNode);
    }

    // cgp codes (e.g. "DET", "INS") repeat across different cte groups, so the
    // map key has to include the parent to keep them distinct.
    const cgpKey = `${cteCode}${PATH_SEPARATOR}${cgpCode}`;
    let cgpNode = cgpMap.get(cgpKey);
    if (!cgpNode) {
      cgpNode = {
        title: cgpCode,
        value: `${CGP_PREFIX}${cgpKey}`,
        children: [],
      };
      cgpMap.set(cgpKey, cgpNode);
      cteNode.children!.push(cgpNode);
    }

    cgpNode.children!.push({
      // The leaf value becomes the filter query value. With a pathProperty it is
      // the full path (what the backend stores in `searchable`); without it we
      // keep the raw option id like the flat select.
      title: certificateName,
      value: leafValue,
    });
  });

  return cteNodes;
};
